'use strict';

const LOADER_MIN_DISPLAY_MS = 500;
const LOADER_SHOW_DELAY_MS = 500;
const SPINNER_STATE_KEY = 'lvzSpinnerState';
const PAGE_NAV_SPINNER_BOUND = 'lvzPageNavigationSpinnerBound';
const GLOBAL_SPINNER_BOUND = 'lvzGlobalSpinnerBound';
const PAGE_LIFECYCLE_HOLD_KEY = 'lvzPageLifecycleSpinnerHold';

// Only header megamenu links that really navigate to another page should trigger
// the page-level loader on click. All other full-page navigations are covered by
// beforeunload or by explicit feature-level calls before window.location changes.
const HEADER_MENU_NAV_SELECTOR = [
	'[data-lvz-header-menu="true"] .navigation-menu-column__inner a[href]',
	'[data-lvz-header-menu="true"] .editor-menu a[href]'
].join(', ');

function isPrimaryButtonClick(event) {
	return !event || event.button === 0;
}

function isModifiedClick(event) {
	return !!(event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey));
}

function isHashOnlyNavigation(url) {
	return url.hash && url.pathname === window.location.pathname && url.search === window.location.search;
}

function getDefaultState() {
	return {
		// Reference counter lets multiple async flows share the same spinner target
		// without removing the veil until the last caller has completed.
		count: 0,
		showTimer: null,
		hideTimer: null,
		shownAt: 0,
		visible: false,
		$veil: null,
		lastFocusedElement: null
	};
}

function getState($target) {
	let state = $target.data(SPINNER_STATE_KEY);

	if (!state) {
		// Spinner state is stored per target so body-level and component-level
		// loaders can coexist without interfering with each other.
		state = getDefaultState();
		$target.data(SPINNER_STATE_KEY, state);
	}

	return state;
}

function clearTimer(timerId) {
	if (timerId) {
		window.clearTimeout(timerId);
	}
}

function isBodyTarget($target) {
	return !!($target && $target.get(0) && $target.get(0).tagName === 'BODY');
}

function isInsideMinicart(target) {
	return !!(target && $(target).closest('.minicart').length);
}

function getSpinnerTemplate() {
	return $('#lvzSpinnerTemplate').first();
}

function isTemplateNode($veil) {
	return !!($veil && $veil.length && $veil.is('#lvzSpinnerTemplate'));
}

function getLiveMessage($target) {
	const $template = getSpinnerTemplate();
	const pageMessage = $template.attr('data-loading-page-label') || '';
	const sectionMessage = $template.attr('data-loading-section-label') || pageMessage;

	return isBodyTarget($target) ? pageMessage : sectionMessage;
}

function resolveTarget() {
	// Normalize every caller to the full-page loader so component-level
	// spinners cannot diverge from the global start/stop lifecycle.
	return $('body');
}

function setPageLifecycleHold(isHeld) {
	$('body')
		.data(PAGE_LIFECYCLE_HOLD_KEY, !!isHeld)
		.attr('data-lvz-page-loader-hold', isHeld ? 'true' : 'false');
}

function hasPageLifecycleHold() {
	const $body = $('body');
	return $body.data(PAGE_LIFECYCLE_HOLD_KEY) === true || $body.attr('data-lvz-page-loader-hold') === 'true';
}

function announceBusyState($target, isBusy) {
	if (!$target.length) {
		return;
	}

	$target.attr('aria-busy', isBusy ? 'true' : 'false');
}

function restoreFocus(state) {
	if (!state || !state.lastFocusedElement) {
		return;
	}

	if (document.contains(state.lastFocusedElement)) {
		try {
			state.lastFocusedElement.focus({ preventScroll: true });
		} catch (e) {
			state.lastFocusedElement.focus();
		}
	}

	state.lastFocusedElement = null;
}

function findInitialPageLoader($root) {
	// Prefer a direct child, but also accept a nested initial loader when markup
	// wrappers (e.g. injected tags before the template) prevent a body-level child match.
	var $direct = $root.children('[data-lvz-initial-page-loader="true"]').first();

	if ($direct.length) {
		return $direct;
	}

	return $root.find('[data-lvz-initial-page-loader="true"]').first();
}

function findPageVeils($root) {
	return $root.find('.lvz-veil');
}

function adoptExistingVeil($target, state) {
	if (!isBodyTarget($target) || state.visible) {
		return false;
	}

	const $veil = findInitialPageLoader($target);

	if (!$veil.length) {
		return false;
	}

	state.$veil = $veil;
	state.visible = true;
	state.shownAt = Date.now();
	state.lastFocusedElement = document.activeElement;
	$veil.addClass('lvz-veil--page').attr({
		tabindex: '-1',
		'aria-hidden': 'false'
	});

	announceBusyState($target, true);

	return true;
}

function getTransferableBodyVeil() {
	const $body = $('body');
	const bodyState = getState($body);

	if (bodyState.$veil && bodyState.$veil.length) {
		return bodyState.$veil;
	}

	return findInitialPageLoader($body);
}

function prepareTargetForVeil($target) {
	if (isBodyTarget($target)) {
		$target.addClass('lvz-scroll-locked');
		return;
	}

	if ($target.css('position') === 'static') {
		$target.addClass('lvz-veiled');
	}
}

// If a page is rendered with an initial fullscreen veil, move that same DOM node
// instead of creating a second loader. This keeps the transition continuous when
// a feature decides to reuse the active loader in a more specific target.
function attachTransferredVeil($target, state, $veil) {
	$veil.removeClass('d-none lvz-veil--page').removeAttr('tabindex').attr({
		'data-lvz-initial-page-loader': 'false',
		'aria-hidden': 'true'
	});

	prepareTargetForVeil($target);
	$target.append($veil);

	state.$veil = $veil;
	state.visible = true;
	state.shownAt = Date.now();

	announceBusyState($target, true);
	setPageLifecycleHold(false);

	return true;
}

function transferInitialPageLoaderToTarget($target, state) {
	if (isBodyTarget($target) || state.visible) {
		return false;
	}

	const $body = $('body');
	const $veil = getTransferableBodyVeil();

	if (!$veil.length) {
		return false;
	}

	const bodyState = getState($body);

	clearTimer(bodyState.showTimer);
	clearTimer(bodyState.hideTimer);

	bodyState.showTimer = null;
	bodyState.hideTimer = null;
	bodyState.$veil = null;
	bodyState.visible = false;
	bodyState.count = 0;
	bodyState.shownAt = 0;

	$body.removeClass('lvz-scroll-locked');
	announceBusyState($body, false);
	restoreFocus(bodyState);

	return attachTransferredVeil($target, state, $veil);
}

// Some pages render a server-side initial loader and mark the body with a hold
// flag. On DOM ready we adopt that veil, or create the body-level veil if the
// initial markup is not available anymore, so the loader remains continuous.
function bootstrapPageHoldSpinner() {
	if (!hasPageLifecycleHold()) {
		return;
	}

	const $body = $('body');
	const bodyState = getState($body);

	if (bodyState.visible) {
		return;
	}

	bodyState.count = Math.max(bodyState.count, 1);

	if (adoptExistingVeil($body, bodyState)) {
		return;
	}

	// If adopt failed while an untracked page veil is still painted, clear it first
	// so createVeil cannot leave two overlapping fullscreen loaders.
	cleanupOrphanedPageVeils($body);
	createVeil($body, bodyState);
}

function createVeil($target, state) {
	if (state.visible) {
		return;
	}

	const liveMessage = getLiveMessage($target);
	const $template = getSpinnerTemplate();
	let $veil;

	if (!$template.length) {
		return;
	}

	$veil = $template.clone();
	$veil.removeAttr('id');
	$veil.removeAttr('hidden');
	$veil.removeClass('d-none');
	$veil.removeClass('lvz-veil--page');
	$veil.attr({
		'data-lvz-spinner-template': 'false',
		'data-lvz-initial-page-loader': 'false'
	});

	$veil.find('.lvz-veil__spinner').attr({
		role: 'status',
		'aria-live': 'polite',
		'aria-atomic': 'true'
	});
	$veil.find('.sr-only').remove();
	$veil.find('.lvz-veil__spinner').append('<span class="sr-only">' + liveMessage + '</span>');

	if ($target.get(0).tagName === 'IMG') {
		$target.after($veil);
		$veil.css({
			width: $target.width(),
			height: $target.height()
		});
		if ($target.parent().css('position') === 'static') {
			$target.parent().css('position', 'relative');
		}
	} else {
		$target.append($veil);

		if (isBodyTarget($target)) {
			$veil.addClass('lvz-veil--page').attr({
				tabindex: '-1',
				'aria-hidden': 'false'
			});
			state.lastFocusedElement = document.activeElement;
		} else {
			$veil.attr('aria-hidden', 'true');
		}
	}

	prepareTargetForVeil($target);

	$veil.on('click', function (e) {
		e.stopPropagation();
	});

	state.$veil = $veil;
	state.visible = true;
	state.shownAt = Date.now();

	announceBusyState($target, true);

}

function destroyVeil($target, state) {
	const $veil = state.$veil;

	clearTimer(state.showTimer);
	clearTimer(state.hideTimer);

	state.showTimer = null;
	state.hideTimer = null;

	if (!$veil || !$veil.length) {
		state.visible = false;
		announceBusyState($target, false);
		restoreFocus(state);
		return;
	}

	if ($target.hasClass('lvz-veiled')) {
		$target.css('position', '');
		$target.removeClass('lvz-veiled');
	}

	$target.removeClass('lvz-scroll-locked');
	announceBusyState($target, false);

	$veil.off('click');
	if (isTemplateNode($veil)) {
		$veil.removeClass('lvz-veil--page').addClass('d-none').attr({
			'data-lvz-spinner-template': 'true',
			'data-lvz-initial-page-loader': 'false',
			'aria-hidden': 'true',
			hidden: 'hidden'
		});
		state.$veil = $veil;
	} else {
		$veil.remove();
		state.$veil = null;
	}

	state.visible = false;
	state.shownAt = 0;

	restoreFocus(state);
}

function showSpinner($target) {
	const state = getState($target);

	clearTimer(state.hideTimer);
	state.hideTimer = null;

	if (state.visible || state.showTimer) {
		return;
	}

	// A small delay avoids flashing the loader for interactions that finish
	// almost immediately.
	state.showTimer = window.setTimeout(function () {
		state.showTimer = null;

		if (state.count <= 0 || state.visible) {
			return;
		}

		createVeil($target, state);
	}, LOADER_SHOW_DELAY_MS);
}

function hideSpinner($target) {
	const state = getState($target);

	clearTimer(state.showTimer);
	state.showTimer = null;

	if (!state.visible) {
		destroyVeil($target, state);
		return;
	}

	const elapsed = Date.now() - state.shownAt;
	const waitTime = Math.max(0, LOADER_MIN_DISPLAY_MS - elapsed);

	// Once visible, keep the veil on screen for a minimum time to reduce flicker.
	clearTimer(state.hideTimer);
	state.hideTimer = window.setTimeout(function () {
		state.hideTimer = null;

		if (state.count > 0) {
			return;
		}

		destroyVeil($target, state);
	}, waitTime);
}

function addSpinner($target) {
	const state = getState($target);

	state.count += 1;

	if (adoptExistingVeil($target, state)) {
		return;
	}

	if (transferInitialPageLoaderToTarget($target, state)) {
		return;
	}

	showSpinner($target);
}

function removeSpinner($target) {
	const state = getState($target);

	state.count = Math.max(0, state.count - 1);

	if (state.count > 0) {
		return;
	}

	hideSpinner($target);
}

function cleanupOrphanedPageVeils($target) {
	if (!isBodyTarget($target)) {
		return;
	}

	findPageVeils($target).each(function () {
		var $veil = $(this);

		if (isTemplateNode($veil)) {
			$veil.removeClass('lvz-veil--page').addClass('d-none').attr({
				'data-lvz-spinner-template': 'true',
				'data-lvz-initial-page-loader': 'false',
				'aria-hidden': 'true',
				hidden: 'hidden'
			});
			return;
		}

		$veil.off('click').remove();
	});
}

function forceStop($target) {
	const state = getState($target);

	state.count = 0;
	destroyVeil($target, state);
	// Guarantees no leftover initial template / cloned page veil remains visible
	// when a previous start() adopted a different node than the one still in DOM.
	cleanupOrphanedPageVeils($target);
	$target.removeClass('lvz-scroll-locked');
	announceBusyState($target, false);
}

// Use this helper for AJAX flows that explicitly own the loader. The request is
// excluded from the global ajaxSend handler and the matching stop is guaranteed
// by always(), including network and HTTP errors.
function requestWithSpinner($target, options) {
	const ajaxOptions = $.extend({}, options, {
		lvzSkipAutoSpinner: true
	});

	addSpinner($target);

	return $.ajax(ajaxOptions).always(function () {
		removeSpinner($target);
	});
}

function shouldHandleNavigationLink(anchor, event) {
	if (!anchor || !anchor.href || !isPrimaryButtonClick(event) || isModifiedClick(event)) {
		return false;
	}

	// If another component already consumed the click (for example opening a submenu
	// instead of navigating), the loader must not start or it would remain visible.
	if (event && event.isDefaultPrevented && event.isDefaultPrevented()) {
		return false;
	}

	if (anchor.target && anchor.target !== '_self') {
		return false;
	}

	if (anchor.hasAttribute('download') || anchor.getAttribute('rel') === 'external') {
		return false;
	}

	const href = anchor.getAttribute('href') || '';

	if (!href || href.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(href)) {
		return false;
	}

	let url;

	try {
		url = new URL(anchor.href, window.location.href);
	} catch (e) {
		return false;
	}

	if (url.origin !== window.location.origin || isHashOnlyNavigation(url)) {
		return false;
	}

	if (anchor.matches(HEADER_MENU_NAV_SELECTOR)) {
		return !anchor.closest('[data-no-loader], .ui-datepicker, .swal2-container, .dd__toggle');
	}

	return false;
}

function bindPageNavigationSpinner() {
	if ($(document.body).data(PAGE_NAV_SPINNER_BOUND)) {
		return;
	}

	$(document.body).data(PAGE_NAV_SPINNER_BOUND, true);

	$(document).on('click', 'a[href]', function (event) {
		if (!shouldHandleNavigationLink(this, event)) {
			return;
		}

		$.spinner().start();
	});

	window.addEventListener('beforeunload', function () {
		$.spinner().start();
	});

	window.addEventListener('pageshow', function () {
		if (hasPageLifecycleHold()) {
			return;
		}

		$.spinner().stop();
		forceStop($('body'));
	});

	window.addEventListener('load', function () {
		if (hasPageLifecycleHold()) {
			return;
		}

		forceStop($('body'));
	});
}

function resolveAjaxTarget(settings) {
	if (settings && settings.lvzSpinnerTarget) {
		return resolveTarget(settings.lvzSpinnerTarget);
	}

	if (settings && settings.context) {
		const $context = resolveTarget(settings.context);

		if ($context.length && !isBodyTarget($context)) {
			return $context;
		}
	}

	return $('body');
}

function hasActiveComponentSpinner() {
	let hasActiveSpinner = false;

	$('body')
		.find('*')
		.each(function () {
		const $element = $(this);
		const state = $element.data(SPINNER_STATE_KEY);

		if (!state || state.count <= 0 || isBodyTarget($element)) {
			return;
		}

		hasActiveSpinner = true;
		return false;
	});

	return hasActiveSpinner;
}

function bindGlobalSpinnerEvents() {
	if ($(document.body).data(GLOBAL_SPINNER_BOUND)) {
		return;
	}

	$(document.body).data(GLOBAL_SPINNER_BOUND, true);

	// Custom events allow feature scripts to opt into the shared spinner without
	// depending on spinner internals.
	$(document).on('loader:show', function (_event) {
		$('body').spinner().start();
	});

	$(document).on('loader:hide', function (_event) {
		$('body').spinner().stop();
	});

	$(document).ajaxSend(function (_event, xhr, settings) {
		if (settings && (settings.lvzSkipAutoSpinner || settings.global === false)) {
			return;
		}

		if (settings && isInsideMinicart(settings.context)) {
			xhr.__lvzSkipAutoSpinner = true;
			return;
		}

		// While a destination page is intentionally holding the fullscreen loader
		// (e.g. Gigya bootstrap on login), do not layer a second auto-spinner on AJAX.
		if (hasPageLifecycleHold()) {
			xhr.__lvzSkipAutoSpinner = true;
			return;
		}

		const $target = resolveAjaxTarget(settings);

		// If a component is already managing its own loader, avoid layering an
		// additional fullscreen spinner on top of it for the same request cycle.
		if (isBodyTarget($target) && hasActiveComponentSpinner()) {
			xhr.__lvzSkipAutoSpinner = true;
			return;
		}

		xhr.__lvzSpinnerTarget = $target;
		$target.spinner().start();
	});

	$(document).ajaxComplete(function (_event, xhr, settings) {
		if ((settings && (settings.lvzSkipAutoSpinner || settings.global === false)) || (xhr && xhr.__lvzSkipAutoSpinner)) {
			return;
		}

		const $target = xhr && xhr.__lvzSpinnerTarget ? xhr.__lvzSpinnerTarget : $('body');
		resolveTarget($target).spinner().stop();
	});

	// Start a page-level loader for native form navigations. AJAX forms that call
	// preventDefault() are ignored here and are instead covered by ajaxSend/Complete
	// or by explicit component-level spinner calls already present in the codebase.
	$(document).on('submit', 'form', function (event) {
		if (event.isDefaultPrevented()) {
			return;
		}

		if ($(this).is('[data-no-loader], [data-lvz-no-loader]')) {
			return;
		}

		$.spinner().start();
	});
}

$.fn.spinner = function () {
	const $element = resolveTarget();

	return {
		start: function () {
			if ($element.length) {
				addSpinner($element);
			}
		},
		stop: function () {
			if ($element.length) {
				removeSpinner($element);
			}
		},
		forceStop: function () {
			if ($element.length) {
				forceStop($element);
			}
		},
		request: function (options) {
			return requestWithSpinner($element, options);
		}
	};
};

$.spinner = function () {
	const $body = $('body');

	return {
		start: function () {
			addSpinner($body);
		},
		stop: function () {
			removeSpinner($body);
		},
		forceStop: function () {
			forceStop($body);
		},
		request: function (options) {
			return requestWithSpinner($body, options);
		},
		holdLifecycle: function () {
			// Prevent automatic pageshow/load cleanup while a destination page still
			// needs to keep the fullscreen spinner visible during async bootstrapping.
			setPageLifecycleHold(true);
		},
		releaseLifecycle: function () {
			setPageLifecycleHold(false);
		}
	};
};

$(document).ready(function () {
	bootstrapPageHoldSpinner();
	bindPageNavigationSpinner();
	bindGlobalSpinnerEvents();
});
